import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { FaShoppingCart } from "react-icons/fa";
import { FiShare2 } from "react-icons/fi";
import Skeleton from "react-loading-skeleton";
import Fade from "react-reveal/Fade";
import { useNavigate, useParams } from "react-router-dom";
import StarRatingComponent from "react-star-rating-component";
import Swal from "sweetalert2";
import { useAppDispatch, useAppSelector } from "../../../redux/hook";
import { addCartProduct } from "../../../redux/slice/useCartSlice";
import { formatCurrency } from "../../../utils/Utils";
import { useLazyFavoriteByUserQuery } from "../../../utils/api/favories/favorie.api";
import { useAddProductCartMutation, useLazyProductBySlugQuery } from "../../../utils/api/product/product.api";
import { ApiBaseUrl } from "../../../utils/http";
import {
  getDisplayableColorVariations,
  getProductPriceLabels,
  getProductPromoRate,
  getUnitPriceForVariation,
} from "../../../utils/productPricing";
import { Color } from "../../../utils/theme";
import { getVendeur } from "../../HomePage/HomePage";
import AvisProduct from "./AvisProduct";
import GaleryProduct, { images } from "./GaleryProduct";
import ProductSimilary from "./ProductSimilary";

interface ProduitDetailContentProps {
  /** Slug du produit. Si absent, on le prend depuis l'URL (mode page web). */
  slug?: string;
  /** true = affiché dans un modal (mobile) : masque certains éléments propres à la page. */
  embedded?: boolean;
  /** Appelé après un ajout au panier réussi (utile pour fermer le modal). */
  onAddedToCart?: () => void;
  /** Appelé après un "Acheter maintenant" réussi (utile pour fermer le modal avant la navigation). */
  onBuyNow?: () => void;
}
interface ProduitDetailContentProps {
  slug?: string;
  embedded?: boolean;
  onAddedToCart?: () => void;
  onBuyNow?: () => void;
  onClose?: () => void;
}

const ProduitDetailContent: React.FC<ProduitDetailContentProps> = ({
  slug: slugProp,
  embedded = false,
  onAddedToCart,
  onBuyNow,
}) => {
  const Vendeur = getVendeur();
  const dispatch = useAppDispatch();

  const [item] = images;

  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugProp || slugParam;

  const { user, token } = useAppSelector((s) => s?.user);
  const [variationActive, setVariationActive] = useState<any>();
  const [variations, setVariations] = useState<any>([]);
  const [validation, setValidation] = useState<boolean>(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [imagesProduit, setImagesProduit] = useState<any>();
  const [tailles, setTailles] = useState<any>();
  const [tailleActive, setTailleActive] = useState<any>();
  const [findBySlug, { data, isError, isLoading }] =
    useLazyProductBySlugQuery();
  const [findByFavorisSlug, { data: favoris }] = useLazyFavoriteByUserQuery();
  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };
  const navigate = useNavigate();
  const [count, setCount] = React.useState(1);
  const [addToCart] = useAddProductCartMutation();
  const maxLength = 324;
  const [isExpanded, setIsExpanded] = useState(false);

  const isTruncated = data?.description?.length > maxLength;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (slug) {
      findBySlug(slug as string);
      findByFavorisSlug(user?.slug as string);
    }
  }, [slug]);

  const handalevalidation = () => {
    setValidation(false);
    setShowValidation(true);

    setTimeout(() => {
      setShowValidation(false);
    }, 3000);
  };

  useEffect(() => {
    const inStock =
      data?.variations?.filter((value) => Number(value.quantite) > 0) ?? [];
    const couleurs = getDisplayableColorVariations(data?.variations);
    let firstVariation =
      couleurs.length > 0
        ? couleurs[0]
        : (inStock.find(
            (value, index, self) =>
              self.findIndex((v) => v.couleur === value.couleur) === index,
          ) ?? inStock[0]);

    if (
      data?.type == "taille_unique" ||
      data?.type == "taille_unique_avec_couleur"
    ) {
      setValidation(true);
      setShowValidation(false);
    }

    if (firstVariation) {
      setVariationActive(firstVariation);
      setVariations([firstVariation]);
    } else {
      setVariationActive(null);
      setVariations([]);
    }
  }, [data]);

  useEffect(() => {
    setImagesProduit(variationActive?.images);
    const inStock =
      data?.variations?.filter((x) => Number(x.quantite) > 0) ?? [];
    const couleurs = getDisplayableColorVariations(data?.variations);
    if (couleurs.length === 0) {
      setTailles(inStock);
    } else {
      setTailles(inStock.filter((x) => x.couleur == variationActive?.couleur));
    }
  }, [variationActive, data]);

  function SuccessPopup() {
    Swal.fire({
      icon: "success",
      iconColor: Color.success,
      confirmButtonColor: Color.success,
      title: "Produit ajouté au panier avec succès !",
      showConfirmButton: false,
      timer: 3000,
    });
  }

  const promoRate = getProductPromoRate(data);
  const selectedVariation = variations?.[0] ?? variationActive;
  const couleursDisponibles = useMemo(
    () => getDisplayableColorVariations(data?.variations),
    [data?.variations],
  );
  const priceLabels = useMemo(
    () => getProductPriceLabels(data, Vendeur?.devise),
    [data, Vendeur?.devise],
  );

  const buildCartPayload = () => ({
    produit: data,
    prix: getUnitPriceForVariation(data, selectedVariation, promoRate),
    quantite: count,
    stock: data?.stock,
    variations: variations,
    vendeur: data?.vendeur?.id,
  });

  const ajouterAuPanier = async () => {
    findBySlug(slug as string);
    dispatch(addCartProduct(buildCartPayload()));
    SuccessPopup();
    onAddedToCart?.();
  };

  const onSelectColor = (item) => {
    setVariationActive(item);
    setVariations([item]);
    if (
      data?.type == "taille_unique" ||
      data?.type == "taille_unique_avec_couleur"
    ) {
      setValidation(true);
    } else {
      setValidation(false);
    }
  };

  const onSelectSize = (item) => {
    setTailleActive(item?.taille);
    setVariations([item]);
    setValidation(true);
  };

  const ColorVariationItem = ({
    color,
    isActive,
    variation,
  }: {
    color: string;
    isActive?: boolean;
    variation?: any;
  }) => {
    return (
      <div
        className={
          variationActive?.id == isActive ? "btn_color_wrapper" : "btn_color"
        }
      >
        <label
          role="button"
          className={`btn btn_color_choose`}
          style={{ background: color }}
          onClick={() => onSelectColor(variation)}
        ></label>

        <input
          type="radio"
          name="variation_color"
          id="variation_color"
          value={variation?.id}
        />
      </div>
    );
  };

  const SizeVariationItem = ({
    size,
    variation,
  }: {
    size: string;
    variation: any;
  }) => {
    return (
      <label
        role="button"
        className={
          variation?.taille == tailleActive
            ? "btn btn_size_choose active "
            : "btn btn_size_choose"
        }
        onClick={() => onSelectSize(variation)}
      >
        <input
          type="radio"
          name="variation_size"
          id="variation_size"
          value={variation?.id}
        />

        {size}
      </label>
    );
  };

  const decrement = (e) => {
    setCount((prevValue) => Math.max(prevValue - 1, 0));
  };

  const increment = (e) => {
    setCount((prevValue) => prevValue + 1);
  };

  const handleChange = (e) => {
    const inputValue = parseInt(e.target.value, 10);

    if (!isNaN(inputValue)) {
      setCount(inputValue);
    } else {
      setCount(0);
    }
  };

  const handleAddFavoris = (id) => {
    let favorie = favoris?.find((x) => x?.produit?.id == id);

    let fd = new FormData();

    fd.append("user", user?.id);
    fd.append("produit", id);

    if (favorie) {
      axios
        .delete(ApiBaseUrl + `/api/favoris/${favorie?.slug}`, config)
        .then((response) => {
          findBySlug(slug as string);
          findByFavorisSlug(user?.slug as string);
        })
        .catch((error) => {
          console.error(`Error: ${error}`);
        });
    } else {
      axios
        .post(ApiBaseUrl + "/api/favoris/", fd, config)
        .then((response) => {
          findBySlug(slug as string);
          findByFavorisSlug(user?.slug as string);
        })
        .catch((error) => {
          console.error(`Error: ${error}`);
        });
    }
  };

  const onToggleFavorite = (id) => {
    if (!user?.slug) {
      Swal.fire({
        icon: "info",
        title: `Veuilliez vous connecter pour ajouter un produit au favoris .`,
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 6000,
      });
    } else {
      handleAddFavoris(id);
    }
  };

  const acheterMaintenant = async () => {
    if (!validation) {
      handalevalidation();
      return;
    }

    let taux =
      data?.categorie?.promotion?.taux ||
      data?.sous_categorie?.promotion?.taux ||
      data?.promotion?.taux;

    let produit = {
      produit: data,
      prix:
        data?.promotion?.is_active ||
        data?.categorie?.promotion?.is_active ||
        data?.sous_categorie?.promotion?.is_active
          ? data?.prix - (data?.prix * taux) / 100
          : parseInt(data?.prix),
      quantite: count,
      stock: data?.stock,
      variations: variations,
      vendeur: data?.vendeur?.id,
    };

    dispatch(addCartProduct(produit));

    onBuyNow?.();
    navigate("/panier");
  };

  const handleShare = async () => {
    const productUrl = `${window.location.origin}/produit/${data?.slug}`;
    const shareText = `Découvrez ${data?.nom} sur Yeksina Home - ${formatCurrency(data?.prix, Vendeur?.devise)}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: data?.nom,
          text: shareText,
          url: productUrl,
        });
      } catch (error) {
        console.log("Partage annulé ou erreur:", error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(productUrl);
        Swal.fire({
          icon: "success",
          title: "Lien copié !",
          text: "Le lien du produit a été copié dans le presse-papiers.",
          iconColor: Color.success,
          showConfirmButton: false,
          timer: 2000,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: "Impossible de copier le lien.",
          iconColor: Color.danger,
        });
      }
    }
  };

  const isFavorite = (id) => {
    let favorie = favoris?.find((x) => x?.produit?.id == id);
    return !!favorie;
  };

  return (
    <div
      className={
        embedded
          ? "container-easy-market embedded-detail"
          : "container-easy-market"
      }
    >
      {isLoading && (
        <div className="row">
          <div className="col-lg-6 col-md-6 mb-3 dis-flex" key={0}>
            <div className="item-stat-dashboad-view ">
              <div className="content-item-stat-dashboad-view mt-2">
                <div className="content-item-icon-stat-dashboard-view">
                  <Skeleton className="img-icon-dash-view" width={`70%`} />
                  <Skeleton className="img-icon-dash-view" width={`70%`} />
                </div>
              </div>
              <div className="content-stat-dashboard-view">
                <div className="content-text-item-stat-dashboad-view">
                  <div className="icon-item-stat-dashboad-view">
                    <Skeleton
                      className="chiffre-item-stat-dashboad-view mb-1"
                      width={`50%`}
                    />
                    <Skeleton
                      className="chiffre-item-stat-dashboad-view mb-1"
                      width={`50%`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6 col-md-6 mb-3 dis-flex" key={0}>
            <div className="item-stat-dashboad-view ">
              <div className="content-item-stat-dashboad-view mt-2">
                <Skeleton
                  className="titre-item-stat-dashboad-view mb-2"
                  width={`70%`}
                />
                <Skeleton
                  className="titre-item-stat-dashboad-view mb-2"
                  width={`50%`}
                />
                <Skeleton
                  className="titre-item-stat-dashboad-view mb-2"
                  width={`30%`}
                />
              </div>
              <div className="content-stat-dashboard-view">
                <div className="content-text-item-stat-dashboad-view">
                  <div className="icon-item-stat-dashboad-view">
                    <Skeleton
                      className="chiffre-item-stat-dashboad-view mb-1"
                      width={`50%`}
                    />
                    <Skeleton
                      className="titre-item-stat-dashboad-view mb-2"
                      width={`70%`}
                    />
                    <Skeleton
                      className="titre-item-stat-dashboad-view mb-2"
                      width={`50%`}
                    />
                    <Skeleton
                      className="chiffre-item-stat-dashboad-view mb-1"
                      width={`50%`}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {!isLoading && (
        <div className="row row-product-item-page b-b-1 mb-3 pb-4">
          <div className="col-lg-6 col-md-12 col-left-product-item-page d-flex">
            <div className="content-col-left-product-item-page container-product-item-page">
              <GaleryProduct
                pictures={
                  data?.type == "taille_unique"
                    ? data?.images
                    : data?.variations && data?.variations[0]?.images
                      ? imagesProduit
                        ? imagesProduit
                        : data?.variations[0]?.images
                      : null
                }
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-12 col-left-product-item-page d-flex">
            <div className="content-col-left-product-item-page container-product-item-page">
              <Fade bottom>
                <h1 className="name-product mb-2">{data?.nom}</h1>
              </Fade>
              <Fade bottom>
                <p className="category-product-name mb-1">
                  Catégorie: {data?.categorie?.nom}
                </p>
              </Fade>

              <div className="custom_container container-product-description pb-4">
                <Fade bottom>
                  <p className="custom_sm__title">Description</p>
                </Fade>
                <Fade bottom>
                  <>
                    <pre className="text-describe-product-item-detail py-3">
                      {isExpanded || !isTruncated
                        ? data?.description
                        : `${data?.description?.slice(0, maxLength)}...`}
                    </pre>
                    {isTruncated && (
                      <button
                        onClick={toggleExpanded}
                        className="btn btn-see-more-product"
                      >
                        {isExpanded ? "Voir moins" : "Voir plus"}
                      </button>
                    )}
                  </>
                </Fade>
              </div>

              <div className="custom_container container-product-price pb-4">
                <Fade bottom>
                  {priceLabels.showRange ? (
                    <>
                      <span className="custom_sm__price pb-3">
                        {priceLabels.display}
                      </span>
                      {priceLabels.strikethrough && (
                        <span className="custom_sm__price_promos_bare pb-3">
                          {priceLabels.strikethrough}
                        </span>
                      )}
                    </>
                  ) : priceLabels.hasPromo ? (
                    <>
                      <span className="custom_sm__price_promos pb-3">
                        {priceLabels.display}
                      </span>
                      <span className="custom_sm__price_promos_bare pb-3">
                        {priceLabels.strikethrough}
                      </span>
                    </>
                  ) : (
                    <p className="custom_sm__price pb-3">
                      {priceLabels.display}
                    </p>
                  )}
                </Fade>
              </div>
              {couleursDisponibles.length > 0 ? (
                <div className="custom_container variation_content container-product-colors pb-4">
                  <Fade bottom>
                    <p className="m-0 pb-2 custom_sm__title">Couleur</p>
                  </Fade>
                  <Fade bottom>
                    <div className="d-flex align-items-center gap-3 avalaible_colors_wrapper">
                      {couleursDisponibles.map((item, key) => (
                        <ColorVariationItem
                          color={item.couleur}
                          isActive={item.id}
                          variation={item}
                          key={key}
                        />
                      ))}
                    </div>
                  </Fade>
                </div>
              ) : (
                ""
              )}
              {data?.type != "taille_unique" &&
              data?.type != "taille_unique_avec_couleur" ? (
                <div className="custom_container variation_content container-product-sizes pb-3">
                  <Fade bottom>
                    <p className="m-0 pb-2 custom_sm__title">Taille</p>
                  </Fade>
                  <Fade bottom>
                    <div className="d-flex align-items-center gap-3 avalaible_sizes_wrapper">
                      {!!tailles &&
                        tailles.map((item, key) => (
                          <SizeVariationItem
                            size={item.taille}
                            variation={item}
                            key={key}
                          />
                        ))}
                    </div>
                  </Fade>
                </div>
              ) : (
                ""
              )}
              {showValidation && (
                <div className="alert alert-danger mt-1" role="alert">
                  Veillez choisir une taille
                </div>
              )}
              <div className="container-quantite-produit mb-4">
                <p className="title-quantity-product">Quantité</p>
                <div className="btn-wrap">
                  <button
                    className="btn decrement-btn btn-counter-item"
                    disabled={count <= 1}
                    onClick={(e) => decrement(e)}
                  >
                    −
                  </button>
                  <div className="num">
                    <input
                      type="number"
                      value={count}
                      onChange={handleChange}
                      className="input-counter-item "
                    />
                  </div>
                  {count < data?.stock &&
                    (data?.type == "taille_unique"
                      ? true
                      : count < parseInt(variationActive?.quantite)) && (
                      <button
                        className="btn increment-btn btn-counter-item"
                        onClick={(e) => increment(e)}
                      >
                        +
                      </button>
                    )}
                </div>
              </div>
              {count > parseInt(variationActive?.quantite) && (
                <div className="alert alert-danger mt-1" role="alert">
                  {parseInt(variationActive?.quantite) > 0
                    ? `La quantité maximale est de ${variationActive?.quantite}`
                    : `Cette variation est en rupture de stock`}
                </div>
              )}
              <div className="container-quantite-produit">
                <p className="title-quantity-product">Total</p>
                <p className="title-quantity-product">
                  {formatCurrency(
                    getUnitPriceForVariation(
                      data,
                      selectedVariation,
                      promoRate,
                    ) * count,
                    Vendeur?.devise,
                  )}
                </p>
              </div>
              <div className="container-btn-add-to-cart-product mt-4">
                <div className="product-action-buttons-row">
                  {count > 0 &&
                  data?.stock > 0 &&
                  (data?.type == "taille_unique"
                    ? true
                    : parseInt(variationActive?.quantite) > 0 &&
                      count <= parseInt(variationActive?.quantite)) ? (
                    <>
                      <button
                        className="btn btn-buy-now"
                        onClick={() =>
                          validation ? acheterMaintenant() : handalevalidation()
                        }
                      >
                        Acheter maintenant
                      </button>
                      <button
                        className="btn btn-add-to-cart"
                        onClick={() =>
                          validation ? ajouterAuPanier() : handalevalidation()
                        }
                      >
                        Ajouter au panier
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-buy-now" disabled={true}>
                        Acheter maintenant
                      </button>
                      <button className="btn btn-add-to-cart" disabled={true}>
                        Ajouter au panier
                      </button>
                    </>
                  )}
                </div>
                <div className="product-secondary-buttons-row">
                  <button className="btn btn-share" onClick={handleShare}>
                    <FiShare2 />
                    <span className="ps-2">Partager</span>
                  </button>
                  <button
                    className="btn btn-favorite"
                    onClick={(e) => onToggleFavorite(data?.id)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1 d-inline-block"
                          role="status"
                        ></span>
                      </>
                    ) : isFavorite(data?.id) ? (
                      <AiFillHeart style={{ color: "#DB242A" }} />
                    ) : (
                      <AiOutlineHeart />
                    )}
                    <span className="ps-2">Favoris</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <AvisProduct product={data} />
      <ProductSimilary product={data} />
    </div>
  );
};

export default ProduitDetailContent;
